/**
 * Settings and themes.
 *
 * SYNCED settings are rows of the `settings` table, one row per top-level key of `SyncedSettings`
 * (so two devices changing different keys never conflict). LOCAL settings are rows of
 * `local_settings` and never leave the device.
 */

import type { DeviceId, EpochMs, FolderId, HexColor, ViewId } from './ids.js';
import type { Entitlement } from './billing.js';

// ── Themes ─────────────────────────────────────────────────────────────────────────────────────

/** Predefined themes. Free gets the first two; Pro gets all five plus 'custom' (billing.md). */
export const PREDEFINED_THEME_IDS = ['dark', 'light', 'paper', 'midnight', 'mono'] as const;
export type PredefinedThemeId = (typeof PREDEFINED_THEME_IDS)[number];
export type ThemeId = PredefinedThemeId | 'custom';

/** The 18 colour roles of the prototype (design/reboot/themes.js `colors`), same keys. */
export const THEME_COLOR_KEYS = [
  'page', 'nav', 'list', 'reader', 'details', 'surface', 'surface-hover', 'border', 'border-light',
  'text', 'body', 'muted', 'dim', 'accent', 'accent-fill', 'accent-bg', 'selected', 'green',
] as const;
export type ThemeColorKey = (typeof THEME_COLOR_KEYS)[number];

export type ThemeFont = 'Inter' | 'system-ui' | 'Verdana' | 'Georgia' | 'ui-serif' | 'ui-monospace' | 'custom';

/**
 * A complete theme as data. This is exactly the prototype's custom-theme object
 * (design/reboot/themes.js `defaults()`/`normalize()`), and the import/export file format is
 * `{ format: 'digester-theme', version: 1, theme: ThemeDefinition }`.
 * Predefined themes are ThemeDefinition constants in packages/web/src/theme/presets.ts.
 * No arbitrary CSS is ever executed: values are validated and written as CSS custom properties.
 */
export interface ThemeDefinition {
  name: string;
  /** Which built-in stylesheet variant the tokens extend. */
  base: 'dark' | 'light';
  uiFont: ThemeFont;
  readingFont: ThemeFont;
  /** Installed font family names, used when the font is 'custom'. Sanitised: no ; { } < > \ . */
  uiFamily: string;
  readingFamily: string;
  shadows: boolean;
  colors: Record<ThemeColorKey, HexColor>;
  // type
  uiSize: number; // 14..20 px
  readingSize: number; // 15..28 px
  headingSize: number; // 24..44 px
  uiLineHeight: number; // 1.2..2
  readingLineHeight: number; // 1.3..2.4
  letterSpacing: number; // -0.5..1 px
  // spacing and dimensions
  rowPadding: number; // 8..32 px
  readerPadding: number; // 18..72 px
  sectionGap: number; // 14..48 px
  controlHeight: number; // 38..56 px
  toolbarHeight: number; // 64..96 px
  radius: number; // 0..20 px
  borderWidth: number; // 0..3 px
  iconSize: number; // 16..26 px
  // desktop panes
  navWidth: number; // 160..250 px
  listWidth: number; // 300..460 px
  detailsWidth: number; // 250..360 px
  articleWidth: number; // 480..900 px
}

export interface ThemeFile {
  format: 'digester-theme';
  version: 1;
  theme: ThemeDefinition;
}

// ── Synced settings ────────────────────────────────────────────────────────────────────────────

export interface ProfileSettings {
  name: string;
  /** IANA zone, e.g. 'Europe/Belgrade'. Used for displaying times only. */
  timezone: string;
  language: 'en' | 'ru' | 'sr';
  /** Notify on new Priority messages (a seam: no notification channel on the web build). */
  notifications: boolean;
}

export interface AppearanceSettings {
  theme: ThemeId;
  accent: 'violet' | 'blue' | 'green' | 'neutral';
  density: 'comfortable' | 'compact';
}

export interface ReadingSettings {
  /** Article text size in px, 15..28. */
  fontSize: number;
  showImages: boolean;
  /** Opening a message marks it read. */
  readOnOpen: boolean;
}

/**
 * LEGACY ALIAS from data schema 3 (models.md §9.3, §9.6): the truth is the Jev model record (`models` row
 * 'jev': its switch, its run rule and its options). The storage layer derives `settings.get().scoring` from
 * it and writes `settings.set('scoring', …)` through to it (only what changed), so the deployed Budget UI and
 * old MCP clients keep working; the `settings` row 'scoring' is read only once, by migration 3.
 */
export interface ScoringSettings {
  /** Master switch for AI scoring (only effective when the plan allows it). */
  enabled: boolean;
  /** Also score messages a filter already placed (never those a filter dismissed or deleted). */
  scoreFiltered: boolean;
  /**
   * The person's daily budget of messages scored by Jev (0..the plan's limit). null = the plan's limit.
   * = the Jev record's `options.dailyBudget`; enforced by the scoring proxy (engine.md §6 stage B, api.md "Scoring proxy").
   */
  dailyLimit: number | null;
  /**
   * How much of each message's text after the title is sent to Jev, in characters (1..scoring.body_max_chars).
   * null = everything up to scoring.body_max_chars. = the Jev record's `options.bodyMaxChars` (null when it is at
   * least the ceiling). The server's jev.body_max_chars stays the hard ceiling.
   */
  bodyChars: number | null;
}

/** One `settings` row per key. Defaults live in packages/core/src/schema/settings-defaults.ts. */
export interface SyncedSettings {
  profile: ProfileSettings;
  appearance: AppearanceSettings;
  reading: ReadingSettings;
  scoring: ScoringSettings;
  customTheme: ThemeDefinition;
}
export type SyncedSettingKey = keyof SyncedSettings;

// ── Local (device-only) settings ───────────────────────────────────────────────────────────────

export interface UiState {
  detailsOpen: boolean;
  sidebarCollapsed: boolean;
  /** The view shown at startup: a folder or a saved view. */
  lastView: { kind: 'folder'; id: FolderId } | { kind: 'view'; id: ViewId };
}

export interface LocalSettings {
  /** This device's node id; generated once, stable for the life of the local database. */
  deviceId: DeviceId;
  ui: UiState;
  /** Last entitlement received from the server; absent = Free. */
  entitlement: Entitlement | null;
  entitlementFetchedAt: EpochMs | null;
  /** The signed-in account, for display. The session token itself is in the SecretStore / cookie. */
  account: { email: string } | null;
  /** Anonymous install token for relay / IMAP bridge when signed out. */
  installToken: string | null;
  /** First run finished (seed data offered). */
  onboarded: boolean;
  // What THIS device was asked to download (wanted / paused / installed) is not a local setting: the
  // on-device runtime keeps it next to the files (web: OPFS `models/manifest.json`, models.md §6.2–6.3).
}
export type LocalSettingKey = keyof LocalSettings;
